import React from 'react';
import { useRouter } from 'next/router';
import Link from 'components/Link';
import { CalIcon } from '../../../svg/CalendarIcon';

import { calendarTabs } from 'constants/calendarTabs';
import classNames from 'styles/utils/classNames';
import { getCalendarPage } from 'constants/pages';
import PageTitle from 'components/PageTitle';

interface CalendarTabsHeaderProps {
  activeSlug: string;
  title?: string;
}

const SYMBOL_BASED_SLUGS = ['earnings', 'dividends', 'splits'];

const CalendarTabsHeader: React.FC<CalendarTabsHeaderProps> = ({ activeSlug, title }) => {
  const router = useRouter();
  const symbol = router.query.symbol as string | undefined;
  const activeTab = calendarTabs.find((tab) => tab.slug === activeSlug);

  return (
    <div className="mb-6">
      <PageTitle
        Icon={CalIcon}
        title={title || (activeTab?.name ? `${activeTab.name} Calendar` : 'Calendar')}
      />

      <div className="hidden justify-center overflow-hidden rounded-md shadow-sm sm:flex">
        {calendarTabs.map((tab, idx) => {
          const supportsSymbol = SYMBOL_BASED_SLUGS.includes(tab.slug);
          const href =
            supportsSymbol && symbol
              ? getCalendarPage(tab.slug, symbol)
              : getCalendarPage(tab.slug);

          const isActive = activeSlug === tab.slug;

          return (
            <Link
              href={href}
              key={tab.slug}
              className={classNames(
                'min-w-[140px] border border-brand-primary px-4 py-3.5 text-center text-sm font-semibold transition-colors duration-200 md:min-w-[172px] md:px-7',
                activeSlug === tab.slug
                  ? 'bg-brand-primary text-white hover:bg-white hover:text-brand-primary'
                  : 'text-white hover:bg-white hover:text-brand-primary',
                idx === 0 ? 'rounded-l-md' : '',
                idx === calendarTabs.length - 1 ? 'rounded-r-md' : '',
                'focus:outline-none',
              )}
              aria-current={isActive ? 'page' : undefined}
              role="tab"
              aria-selected={isActive}
            >
              <span className="relative z-10">{tab.name}</span>
              {supportsSymbol && symbol && (
                <span className="ml-2 inline-flex items-center rounded-full bg-white bg-opacity-20 px-1.5 py-0.5 text-xs">
                  {symbol}
                </span>
              )}
            </Link>
          );
        })}
      </div>
      <div className="space-y-2 px-2 sm:hidden">
        {calendarTabs.map((tab) => {
          const supportsSymbol = SYMBOL_BASED_SLUGS.includes(tab.slug);
          const href =
            supportsSymbol && symbol
              ? getCalendarPage(tab.slug, symbol)
              : getCalendarPage(tab.slug);

          const isActive = activeSlug === tab.slug;

          return (
            <Link
              href={href}
              key={tab.slug}
              className={classNames(
                'flex w-full items-center justify-between rounded-md border border-brand-primary px-4 py-3.5 text-sm font-semibold transition-all duration-200',
                isActive ? 'bg-brand-primary text-white' : 'text-white',
              )}
              aria-current={isActive ? 'page' : undefined}
              role="tab"
              aria-selected={isActive}
            >
              <span>{tab.name}</span>

              {supportsSymbol && symbol && (
                <span className="inline-flex items-center rounded-full bg-white bg-opacity-20 px-2 py-0.5 text-xs font-medium">
                  {symbol}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarTabsHeader;
